import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { STAGE_LABELS } from '@nemo/core/model';
import { conflictText } from '../grid/fundColumns.js';
import { useTRPC } from '../trpc.js';
import { formatDay } from '../ui/format.js';
import { useToast } from '../ui/Toast.js';
import { CollapsibleCard } from './CollapsibleCard.js';
import { invalidateFundRows } from '../grid/invalidateFundRows.js';

/**
 * The stage answers people gave that evidence arriving since disagrees with:
 * "you said it does not lead at Series A; since then it led one". The answer
 * still decides what the list shows. Keeping it says it again, so it is newer
 * than the evidence; accepting says what the evidence says. Either is a new
 * observation, never an edit.
 */
export function StageConflicts(): React.JSX.Element {
  const trpc = useTRPC();
  const toast = useToast();
  const queryClient = useQueryClient();
  const conflicts = useQuery(trpc.funds.conflicts.queryOptions());

  const resolve = useMutation(
    trpc.funds.resolveConflict.mutationOptions({
      onSuccess: () => {
        invalidateFundRows(queryClient, trpc);
      },
      onError: (error: { message: string }) => toast(error.message),
    }),
  );

  const rows = conflicts.data ?? [];

  return (
    <CollapsibleCard id="conflicts" title="Conflicts" count={rows.length}>
      <p className="subtle">
        {rows.length === 0
          ? 'No answer is contradicted by newer evidence.'
          : 'Answers somebody gave that evidence seen since disagrees with.'}
      </p>
      {rows.map((row) => {
        const key = { fundId: row.fundId, stage: row.stage, attribute: row.attribute };
        return (
          <div className="review-row" key={`${row.fundId}:${row.stage}:${row.attribute}`}>
            <p className="review-said">
              <strong>{row.fundName}</strong> · {STAGE_LABELS[row.stage]} · answered{' '}
              {formatDay(row.since)}
            </p>
            <p className="subtle">{conflictText(row, row.stage)}</p>
            <div className="review-actions">
              <button
                className="button small"
                type="button"
                disabled={resolve.isPending}
                onClick={() => resolve.mutate({ ...key, resolution: 'keep' })}
              >
                Keep our answer
              </button>
              <button
                className="button small"
                type="button"
                disabled={resolve.isPending}
                onClick={() => resolve.mutate({ ...key, resolution: 'accept' })}
              >
                Accept the evidence
              </button>
            </div>
          </div>
        );
      })}
    </CollapsibleCard>
  );
}
